import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FaUsers, 
  FaCogs, 
  FaChartLine, 
  FaRupeeSign,
  FaArrowUp,
  FaArrowDown,
  FaCalendarAlt
} from 'react-icons/fa';
import { getDashboardStats } from '../store/statsSlice';

const DashboardStats = ({ userRole = 'Admin' }) => {
  const dispatch = useDispatch();
  const { dashboardStats, loading, error } = useSelector((state) => state.stats);

  useEffect(() => {
    // Fetch actual dashboard statistics from API
    dispatch(getDashboardStats());
  }, [dispatch, userRole]);

  // Calculate trend percentages
  const calculateTrend = (current, previous) => {
    if (previous === 0) return { trend: 'up', value: current > 0 ? '+100%' : '0%' };
    const percentage = ((current - previous) / previous) * 100;
    return {
      trend: percentage >= 0 ? 'up' : 'down',
      value: `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`
    };
  };

  const todayTrend = calculateTrend(dashboardStats.todaySales, dashboardStats.yesterdaySales);
  const monthTrend = calculateTrend(dashboardStats.thisMonthSales, dashboardStats.lastMonthSales);

  const StatCard = ({ title, salesValue, profitValue, icon: Icon, color, trend, trendValue }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 hover:shadow-xl transition-shadow duration-300" 
         style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-gray-600 text-sm font-medium uppercase tracking-wider">{title}</p>
          {loading ? (
            <div className="space-y-2 mt-2">
              <div className="animate-pulse bg-gray-200 h-6 w-24 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-5 w-20 rounded"></div>
            </div>
          ) : (
            <div className="mt-2">
              <div className="flex items-center space-x-4">
                <div>
                  <p className="text-2xl font-bold text-gray-800">{salesValue}</p>
                  <p className="text-xs text-gray-500">Sales</p>
                </div>
                <div className="text-gray-300">|</div>
                <div>
                  <p className="text-xl font-bold text-green-600">{profitValue}</p>
                  <p className="text-xs text-gray-500">Profit</p>
                </div>
              </div>
            </div>
          )}
          {trend && (
            <div className={`flex items-center mt-3 text-sm ${
              trend === 'up' ? 'text-green-600' : 'text-red-600'
            }`}>
              {trend === 'up' ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className="p-4 rounded-full" style={{ backgroundColor: `${color}20` }}>
          <Icon className="text-2xl" style={{ color }} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Main Sales & Profit Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard
          title="Today's Performance"
          salesValue={`₹${dashboardStats.todaySales.toLocaleString()}`}
          profitValue={`₹${dashboardStats.todayProfit.toLocaleString()}`}
          icon={FaRupeeSign}
          color="#3B82F6"
          trend={todayTrend.trend}
          trendValue={`${todayTrend.value} from yesterday`}
        />
        
        <StatCard
          title="Yesterday's Performance"
          salesValue={`₹${dashboardStats.yesterdaySales.toLocaleString()}`}
          profitValue={`₹${dashboardStats.yesterdayProfit.toLocaleString()}`}
          icon={FaChartLine}
          color="#10B981"
          trend="up"
          trendValue={`${dashboardStats.yesterdayCount} transactions`}
        />
      </div>

      {/* Monthly Data Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard
          title="This Month Performance"
          salesValue={`₹${dashboardStats.thisMonthSales.toLocaleString()}`}
          profitValue={`₹${dashboardStats.thisMonthProfit.toLocaleString()}`}
          icon={FaCalendarAlt}
          color="#06B6D4"
          trend={monthTrend.trend}
          trendValue={`${monthTrend.value} from last month`}
        />
        
        <StatCard
          title="Last Month Performance"
          salesValue={`₹${dashboardStats.lastMonthSales.toLocaleString()}`}
          profitValue={`₹${dashboardStats.lastMonthProfit.toLocaleString()}`}
          icon={FaCalendarAlt}
          color="#8B5CF6"
          trend="up"
          trendValue={`${dashboardStats.lastMonthCount} transactions`}
        />
      </div>
    </div>
  );
};

export default DashboardStats;
